import type { Appearance, Stripe } from "@stripe/stripe-js";
import { useEffect, useMemo, useRef, useState } from "react";
import type { RouterOutputs } from "~/services/offstreetApi/types";

type UseStripeProps = {
	paymentSession?: RouterOutputs["paymentSession"]["createPaymentSessionPublic"]["session"];
};

export const useStripe = ({ paymentSession }: UseStripeProps) => {
	const isStripeLoaded = useRef<boolean>(false);
	const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | undefined>();

	const { accountId, isMoR } = paymentSession ?? {};

	useEffect(() => {
		if (isStripeLoaded.current === false && paymentSession) {
			const stripeAccount = !isMoR ? accountId : undefined;
			setStripePromise(
				import("@stripe/stripe-js").then((m) =>
					m.loadStripe(
						`${paymentSession.client.publicKey}`,
						stripeAccount
							? { stripeAccount, developerTools: { assistant: { enabled: false } } }
							: { developerTools: { assistant: { enabled: false } } },
					),
				),
			);
			isStripeLoaded.current = true;
		}
	}, [paymentSession, isMoR, accountId]);

	const styles = getComputedStyle(document.documentElement);

	const appearance = useMemo(() => {
		return {
			theme: "stripe",
			labels: "above",
			variables: {
				colorPrimary: `rgb(${styles.getPropertyValue("--os-color-fg-primary")})`,
				fontWeightNormal: "300",
				fontLineHeight: "22px",
				fontSizeSm: `rgb(${styles.getPropertyValue("--font-size-sm")})`,
				focusOutline: "none",
				focusBoxShadow: "none",
				colorText: `rgb(${styles.getPropertyValue("--os-color-neutral-900")})`,
			},
			rules: {
				".Input:focus": {
					border: `1px solid rgb(${styles.getPropertyValue("--os-color-fg-primary")})`,
				},
			},
		} satisfies Appearance;
	}, [styles]);

	return { stripePromise, appearance };
};
